/**
 * Inline PDF viewer for workflow split view (PDF.js canvas — works on tablet Safari/Chrome).
 */

import { GlobalWorkerOptions, getDocument, type PDFDocumentProxy } from 'pdfjs-dist';

// .js not .mjs — avoids nginx mime.types gap + SW cacheFirst stale octet-stream
GlobalWorkerOptions.workerSrc = '/static/libs/pdfjs/pdf.worker.min.js';

export interface WfPdfZoomLimits {
    min: number;
    max: number;
}

export interface WfPdfViewerHandle {
    goToPage(page: number): Promise<void>;
    goToDoc(docId: number, page: number): Promise<void>;
    zoom(delta: number): void;
    resetZoom(): void;
    getZoom(): number;
    getZoomLimits(): WfPdfZoomLimits;
    destroy(): void;
}

/** Baseline multiplier: 1 = whole page visible in panel (fit-page). */
const WF_PDF_FIT_PAGE_ZOOM = 1;
/** Max render scale = fit-width × this factor (relative to fit-page baseline). */
const WF_PDF_MAX_WIDTH_FACTOR = 3;
const WF_PDF_ZOOM_STEP = 0.15;
const WF_PDF_PAD = 8;

type WfPdfViewerOpts = {
    container: HTMLElement;
    docId: number;
    page: number;
    /** URL direct către PDF — pentru fișiere care nu sunt manuale din
     *  /api/documents (ex. raportul lunar generat). Are prioritate față de docId. */
    fileUrl?: string;
    /** Text afișat cât timp se încarcă (implicit: „manualul"). */
    loadingLabel?: string;
    onPageChange?: (page: number, totalPages: number) => void;
    onZoomChange?: (zoom: number) => void;
};

function _wfPdfFileUrl(docId: number): string {
    return '/api/documents/' + docId + '/file';
}

export function mountWfPdfViewer(opts: WfPdfViewerOpts): WfPdfViewerHandle {
    const container = opts.container;
    container.classList.add('wf-pdf-panel');
    container.innerHTML =
        '<div class="wf-pdf-status" id="wf-pdf-status">Se încarcă manualul…</div>'
        + '<div class="wf-pdf-viewport" id="wf-pdf-viewport" hidden>'
        + '<div class="wf-pdf-canvas-wrap" id="wf-pdf-canvas-wrap">'
        + '<canvas id="wf-pdf-canvas"></canvas>'
        + '</div>'
        + '<div class="wf-pdf-thumbs" id="wf-pdf-thumbs" role="list" aria-label="Miniaturi pagini"></div>'
        + '</div>';

    const statusEl = container.querySelector('#wf-pdf-status') as HTMLElement;
    const viewportEl = container.querySelector('#wf-pdf-viewport') as HTMLElement;
    const canvasWrap = container.querySelector('#wf-pdf-canvas-wrap') as HTMLElement;
    const canvas = container.querySelector('#wf-pdf-canvas') as HTMLCanvasElement;
    const thumbsEl = container.querySelector('#wf-pdf-thumbs') as HTMLElement;
    const ctx = canvas.getContext('2d');

    let docId = opts.docId;
    let fileUrl = opts.fileUrl || '';
    let currentPage = Math.max(1, opts.page || 1);
    let pdfDoc: PDFDocumentProxy | null = null;
    let totalPages = 0;
    /** Multiplier on fit-page baseline (1 = whole page visible in panel). */
    let zoomMultiplier = WF_PDF_FIT_PAGE_ZOOM;
    /** Updated each render — max multiplier for current page + panel size. */
    let zoomMaxMultiplier = WF_PDF_MAX_WIDTH_FACTOR;
    let lastPageWidth = 0;
    let lastPageHeight = 0;
    let destroyed = false;
    let renderTask: { cancel?: () => void } | null = null;
    let loadGen = 0;

    function setStatus(msg: string, isError?: boolean): void {
        statusEl.textContent = msg;
        statusEl.classList.toggle('wf-pdf-status-error', !!isError);
        statusEl.hidden = false;
        statusEl.style.display = '';
        viewportEl.hidden = true;
    }

    function showViewport(): void {
        statusEl.hidden = true;
        statusEl.style.display = 'none';
        viewportEl.hidden = false;
    }

    /** Wait until flex layout gives the canvas wrap usable dimensions. */
    function waitForLayout(el: HTMLElement, maxMs = 800): Promise<void> {
        return new Promise(function(resolve) {
            const t0 = Date.now();
            function tick(): void {
                if (el.clientWidth > 80 && el.clientHeight > 80) {
                    resolve();
                    return;
                }
                if (Date.now() - t0 > maxMs) {
                    resolve();
                    return;
                }
                requestAnimationFrame(tick);
            }
            requestAnimationFrame(function() { requestAnimationFrame(tick); });
        });
    }

    async function loadDocument(id: number): Promise<void> {
        const gen = ++loadGen;
        if (pdfDoc) {
            try { await pdfDoc.destroy(); } catch (_e) { /* ignore */ }
            pdfDoc = null;
        }
        setStatus('Se încarcă ' + (opts.loadingLabel || 'manualul') + '…');
        try {
            const loadingTask = getDocument({
                url: fileUrl || _wfPdfFileUrl(id),
                withCredentials: true,
            });
            const doc = await loadingTask.promise;
            if (destroyed || gen !== loadGen) {
                try { await doc.destroy(); } catch (_e) { /* ignore */ }
                return;
            }
            pdfDoc = doc;
            totalPages = doc.numPages;
            if (currentPage > totalPages) currentPage = totalPages;
            buildThumbnails();
            showViewport();
            await waitForLayout(canvasWrap);
            await renderPage();
        } catch (err: unknown) {
            if (destroyed || gen !== loadGen) return;
            const detail = err instanceof Error ? err.message : String(err);
            setStatus('Nu s-a putut încărca manualul. ' + detail, true);
        }
    }

    function panelSize(): { w: number; h: number } {
        const style = getComputedStyle(canvasWrap);
        const padX = parseFloat(style.paddingLeft) + parseFloat(style.paddingRight);
        const padY = parseFloat(style.paddingTop) + parseFloat(style.paddingBottom);
        return {
            w: Math.max(0, canvasWrap.clientWidth - padX - WF_PDF_PAD),
            h: Math.max(0, canvasWrap.clientHeight - padY - WF_PDF_PAD),
        };
    }

    function fitWidthScale(pageWidth: number): number {
        const { w } = panelSize();
        if (w <= 0 || pageWidth <= 0) return 1;
        return w / pageWidth;
    }

    function fitPageScale(pageWidth: number, pageHeight: number): number {
        const { w, h } = panelSize();
        if (w <= 0 || h <= 0 || pageWidth <= 0 || pageHeight <= 0) return 1;
        return Math.min(w / pageWidth, h / pageHeight);
    }

    function fitWidthMultiplier(pageWidth: number, pageHeight: number): number {
        const fitPage = fitPageScale(pageWidth, pageHeight);
        const fitWidth = fitWidthScale(pageWidth);
        if (fitPage <= 0) return 1;
        return fitWidth / fitPage;
    }

    function updateZoomMax(pageWidth: number, pageHeight: number): void {
        const fitPage = fitPageScale(pageWidth, pageHeight);
        const fitWidth = fitWidthScale(pageWidth);
        zoomMaxMultiplier = fitPage > 0
            ? (fitWidth * WF_PDF_MAX_WIDTH_FACTOR) / fitPage
            : WF_PDF_MAX_WIDTH_FACTOR;
    }

    function clampZoomMultiplier(next: number): number {
        return Math.max(WF_PDF_FIT_PAGE_ZOOM, Math.min(zoomMaxMultiplier, next));
    }

    function updateCanvasAlignment(canvasW: number, canvasH: number): void {
        const { w, h } = panelSize();
        const fits = canvasW <= w && canvasH <= h;
        canvasWrap.classList.toggle('wf-pdf-canvas-centered', fits);
    }

    function notifyZoomChange(): void {
        opts.onZoomChange?.(zoomMultiplier);
    }

    async function renderPage(): Promise<void> {
        if (!pdfDoc || !ctx || destroyed) return;
        if (renderTask && renderTask.cancel) {
            try { renderTask.cancel(); } catch (_e) { /* ignore */ }
        }
        try {
            const page = await pdfDoc.getPage(currentPage);
            const pageWidth = page.view[2];
            const pageHeight = page.view[3];
            lastPageWidth = pageWidth;
            lastPageHeight = pageHeight;
            updateZoomMax(pageWidth, pageHeight);
            zoomMultiplier = clampZoomMultiplier(zoomMultiplier);
            const fitPage = fitPageScale(pageWidth, pageHeight);
            const scale = fitPage * zoomMultiplier;
            const viewport = page.getViewport({ scale });
            canvas.width = Math.floor(viewport.width);
            canvas.height = Math.floor(viewport.height);
            canvas.style.width = canvas.width + 'px';
            canvas.style.height = canvas.height + 'px';
            canvasWrap.dataset.zoom = String(zoomMultiplier);
            canvasWrap.style.transform = '';
            canvasWrap.style.transformOrigin = '';
            updateCanvasAlignment(canvas.width, canvas.height);
            const task = page.render({ canvasContext: ctx, viewport });
            renderTask = task;
            await task.promise;
            renderTask = null;
            highlightThumb();
            opts.onPageChange?.(currentPage, totalPages);
            notifyZoomChange();
        } catch (err: unknown) {
            if (destroyed) return;
            if (err && typeof err === 'object' && (err as { name?: string }).name === 'RenderingCancelledException') return;
            setStatus('Eroare la afișarea paginii.', true);
        }
    }

    function highlightThumb(): void {
        thumbsEl.querySelectorAll('.wf-pdf-thumb').forEach(function(el) {
            const btn = el as HTMLElement;
            const p = parseInt(btn.dataset.page || '0', 10);
            btn.classList.toggle('active', p === currentPage);
        });
    }

    function buildThumbnails(): void {
        thumbsEl.innerHTML = '';
        if (!totalPages) return;
        const maxThumbs = Math.min(totalPages, 24);
        const start = Math.max(1, currentPage - 6);
        const end = Math.min(totalPages, start + maxThumbs - 1);
        for (let p = start; p <= end; p++) {
            const btn = document.createElement('button');
            btn.type = 'button';
            btn.className = 'wf-pdf-thumb';
            btn.dataset.page = String(p);
            btn.setAttribute('role', 'listitem');
            btn.title = 'Pagina ' + p;
            btn.innerHTML = '<span class="wf-pdf-thumb-num">' + p + '</span>';
            btn.addEventListener('click', function() {
                void goToPage(p);
            });
            thumbsEl.appendChild(btn);
            void renderThumbCanvas(p, btn);
        }
    }

    async function renderThumbCanvas(pageNum: number, btn: HTMLButtonElement): Promise<void> {
        if (!pdfDoc || destroyed) return;
        try {
            const page = await pdfDoc.getPage(pageNum);
            const vp = page.getViewport({ scale: 0.12 });
            const tc = document.createElement('canvas');
            tc.width = Math.floor(vp.width);
            tc.height = Math.floor(vp.height);
            const tctx = tc.getContext('2d');
            if (!tctx) return;
            await page.render({ canvasContext: tctx, viewport: vp }).promise;
            btn.insertBefore(tc, btn.firstChild);
        } catch (_e) { /* thumbnail optional */ }
    }

    async function goToPage(page: number): Promise<void> {
        if (!pdfDoc) return;
        currentPage = Math.max(1, Math.min(page, totalPages || page));
        await renderPage();
        buildThumbnails();
    }

    async function goToDoc(id: number, page: number): Promise<void> {
        docId = id;
        fileUrl = '';   // navigare la un manual → părăsim URL-ul direct
        currentPage = Math.max(1, page || 1);
        zoomMultiplier = WF_PDF_FIT_PAGE_ZOOM;
        notifyZoomChange();
        await loadDocument(docId);
    }

    let resizeTimer: ReturnType<typeof setTimeout> | null = null;
    const scheduleReflow = function(): void {
        if (resizeTimer) clearTimeout(resizeTimer);
        resizeTimer = setTimeout(function() { void renderPage(); }, 150);
    };
    window.addEventListener('resize', scheduleReflow);
    let panelObserver: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') {
        panelObserver = new ResizeObserver(scheduleReflow);
        panelObserver.observe(canvasWrap);
    }

    canvasWrap.addEventListener('dblclick', function(ev) {
        if ((ev.target as HTMLElement).closest('.wf-pdf-thumb')) return;
        if (lastPageWidth <= 0 || lastPageHeight <= 0) {
            zoomMultiplier = WF_PDF_FIT_PAGE_ZOOM;
        } else {
            const fwMult = fitWidthMultiplier(lastPageWidth, lastPageHeight);
            const nearFitPage = Math.abs(zoomMultiplier - WF_PDF_FIT_PAGE_ZOOM) < 0.02;
            const nearFitWidth = Math.abs(zoomMultiplier - fwMult) < 0.02;
            if (nearFitPage) {
                zoomMultiplier = clampZoomMultiplier(fwMult);
            } else if (nearFitWidth) {
                zoomMultiplier = WF_PDF_FIT_PAGE_ZOOM;
            } else {
                zoomMultiplier = WF_PDF_FIT_PAGE_ZOOM;
            }
        }
        notifyZoomChange();
        void renderPage();
    });

    void loadDocument(docId);

    return {
        goToPage,
        goToDoc,
        zoom: function(delta: number) {
            zoomMultiplier = clampZoomMultiplier(zoomMultiplier + delta);
            notifyZoomChange();
            void renderPage();
        },
        resetZoom: function() {
            zoomMultiplier = WF_PDF_FIT_PAGE_ZOOM;
            notifyZoomChange();
            void renderPage();
        },
        getZoom: function() { return zoomMultiplier; },
        getZoomLimits: function() {
            return { min: WF_PDF_FIT_PAGE_ZOOM, max: zoomMaxMultiplier };
        },
        destroy: function() {
            destroyed = true;
            loadGen++;
            window.removeEventListener('resize', scheduleReflow);
            if (panelObserver) {
                panelObserver.disconnect();
                panelObserver = null;
            }
            if (renderTask && renderTask.cancel) {
                try { renderTask.cancel(); } catch (_e) { /* ignore */ }
            }
            if (pdfDoc) {
                pdfDoc.destroy().catch(function() { /* ignore */ });
                pdfDoc = null;
            }
            container.innerHTML = '';
        },
    };
}

/** Sync toolbar zoom buttons with viewer state (disable at min/max). */
export function syncWfPdfZoomButtons(
    viewer: WfPdfViewerHandle | null,
    zoomInBtn: HTMLElement | null,
    zoomOutBtn: HTMLElement | null,
): void {
    if (!viewer) return;
    const z = viewer.getZoom();
    const limits = viewer.getZoomLimits();
    if (zoomOutBtn) {
        zoomOutBtn.toggleAttribute('disabled', z <= limits.min);
        zoomOutBtn.setAttribute('aria-disabled', z <= limits.min ? 'true' : 'false');
    }
    if (zoomInBtn) {
        zoomInBtn.toggleAttribute('disabled', z >= limits.max);
        zoomInBtn.setAttribute('aria-disabled', z >= limits.max ? 'true' : 'false');
    }
}

/** @deprecated Use WfPdfViewerHandle.zoom — CSS transform zoom removed. */
export function wireWfPdfZoom(_viewportEl: HTMLElement | null, _delta: number): void {
    /* no-op: zoom must go through mountWfPdfViewer handle (PDF.js render scale). */
}
